/**
 * The base schemas every module builds on. `.docs/00-index.md` §5: "API validates
 * and SPA forms resolve with the same object."
 *
 * Every field carries `.describe()`. Session 05 turns these into the MCP tool
 * inputs and agents read the descriptions to decide what to send
 * (`ARCHITECTURE.md` §Naming: "`describe()` on every field").
 *
 * Each schema is validated by the same predicate the rest of the package uses, so
 * `PeriodSchema.parse(…)` hands back a branded `Period` and nothing has to check
 * twice. There is no `.transform()` anywhere here: session 05 converts these to
 * JSON Schema, and a transform would make the input shape disagree with the output.
 */
import { z } from 'zod';
import { DATE_ONLY_PATTERN, PERIOD_PATTERN, isDateOnly, isPeriod, } from '../dates.js';
import { CURRENCIES } from '../money.js';
export const CurrencySchema = z
    .enum(CURRENCIES)
    .describe('Billing currency, ISO 4217. USD or EUR only — a client is billed in one of the two, ' +
    'fixed at onboarding, never in a local currency (PRICING.md).');
/**
 * `strictObject`, not `object`, and that is not a style choice.
 *
 * This is nested inside tool inputs (`agents_set_money_limit`, every `$` action from
 * session 11 on), and `z.strictObject` makes the object it is called on strict and
 * nothing below it. As a plain `z.object` a misspelt `currancy` would be dropped in
 * silence and the call would run with the wrong currency — the exact failure
 * `.docs/05-actions-mcp-agents.md` §Edge cases has strict inputs for.
 */
export const MoneySchema = z
    .strictObject({
    amount: z
        .int()
        .describe('Amount in minor units — cents for both USD and EUR. 318000 is $3,180.00. ' +
        'Never a decimal, never a float. Negative for a discount or a credit.'),
    currency: CurrencySchema,
})
    .describe('An amount of money: minor units and the currency, always as a pair.');
export const PeriodSchema = z
    .string()
    .regex(PERIOD_PATTERN, 'A period is YYYY-MM, for example 2026-08.')
    .refine(isPeriod)
    .describe('A billing period as YYYY-MM, for example 2026-08. Periods are UTC months: an event ' +
    'at 2026-08-31T23:30:00-04:00 falls in 2026-09.');
export const DateOnlySchema = z
    .string()
    .regex(DATE_ONLY_PATTERN, 'A date is YYYY-MM-DD, for example 2026-08-06.')
    .refine(isDateOnly, 'That day does not exist in that month.')
    .describe('A calendar date as YYYY-MM-DD, for example 2026-08-06. No time, no zone.');
export const Rfc3339Schema = z.iso
    .datetime({ offset: true })
    .describe('An instant as RFC 3339 with an offset, for example 2026-08-12T14:22:00Z. The API ' +
    'speaks UTC; the portal renders it in the viewer’s zone.');
export const IdempotencyKeySchema = z
    .string()
    .min(1)
    .max(200)
    .describe('A key that makes a retry safe: the same key from the same caller returns the stored ' +
    'response instead of charging twice, for a day, and runs nothing. So it is for one ' +
    'call and its retries, and a new call takes a key no earlier one has used. Agents ' +
    'pass "<task_ref>:<step>", for example "TSK-1042:approve", and number a step they ' +
    'repeat ("TSK-1042:comment:2").');
//# sourceMappingURL=primitives.js.map